import type {
  SettingsUpdatePayload,
  SettingsUpdateVariables,
} from "./settings.type";
import type { ContextSlug } from "@/entities/context";
import type { ApiEmptyResponse, ApiError } from "@/services/api";

import {
  useIsMutating,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { client } from "@/config/client";

import {
  CURRENT_USER_SETTINGS_QUERY_KEY,
  UPDATE_CONTEXT_MUTATION_KEY,
} from "./settings.constant";

export const useUpdateSettings = () => {
  const queryClient = useQueryClient();

  const mutationFn = (data: SettingsUpdateVariables) => {
    return client.settings.update(data);
  };

  const onSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: CURRENT_USER_SETTINGS_QUERY_KEY,
    });
  };

  return useMutation<SettingsUpdatePayload, ApiError, SettingsUpdateVariables>({
    mutationFn,
    onSuccess,
  });
};

export const useUpdateSettingsContext = () => {
  const queryClient = useQueryClient();

  const mutationFn = (context: ContextSlug) => {
    return client.context.update(context);
  };

  const onSuccess = () => {
    // return invalidateQueries promise to let mutation in pending state
    // until all the related queries have finish fetching new data.
    const queryKey = CURRENT_USER_SETTINGS_QUERY_KEY;
    return queryClient.invalidateQueries({ queryKey });
  };

  return useMutation<ApiEmptyResponse, ApiError, ContextSlug>({
    mutationKey: UPDATE_CONTEXT_MUTATION_KEY,
    mutationFn,
    onSuccess,
  });
};

export const useIsSettingsContextMutating = () => {
  const mutationKey = UPDATE_CONTEXT_MUTATION_KEY;
  const mutationCount = useIsMutating({ mutationKey });

  return mutationCount > 0;
};
